import { useMemo } from 'react'
import styled from 'styled-components'
import { Modal, Form, Select } from 'antd'
import { FormCard, FormItem, Heading, TextArea, Button } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { showToast } from 'common/utils'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { StockRecord, AdministrationStatus } from '../../types'
import { useUpdateAdministrationRecord } from '../../hooks/useUpdateAdministrationRecord'
import {
  getAdministrationStatusOptions,
  getAdministrationStatusDisplay
} from '../../utils/administrationUtils'

type AdministrationModalProps = {
  open: boolean
  medicationName: string
  record: StockRecord | undefined
  onCancel: () => void
  onSuccess?: () => void
}

const AdministrationModal = ({
  open,
  medicationName,
  record,
  onCancel,
  onSuccess
}: AdministrationModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const [form] = Form.useForm()

  const adminData = record?.administration
  const currentStatus =
    (adminData?.administrationStatus as AdministrationStatus) ||
    (adminData?.reasonCode as AdministrationStatus) ||
    AdministrationStatus.GIVEN
  const currentNote = adminData?.note || record?.notes || ''
  const noteText = Array.isArray(currentNote)
    ? currentNote.join(', ')
    : currentNote

  const statusOptions = getAdministrationStatusOptions(t)

  const initialValues = useMemo(
    () => ({
      outcome: currentStatus,
      comment: noteText
    }),
    [currentStatus, noteText]
  )

  const { updateAdministrationRecord, isLoading } =
    useUpdateAdministrationRecord({
      onSuccess: () => {
        showToast({
          type: 'success',
          title: t('ADMINISTRATION_RECORD_UPDATED_SUCCESS')
        })
        onSuccess?.()
      },
      onError: () => {
        showToast({
          type: 'error',
          title: t('ADMINISTRATION_RECORD_UPDATE_ERROR')
        })
      }
    })

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  const handleSave = () => {
    form.validateFields().then((values) => {
      if (!record?.id) return

      updateAdministrationRecord({
        recordId: record.id,
        data: {
          administrationStatus: values.outcome,
          note: values.comment || null
        }
      })
    })
  }

  const currentStatusDisplay = getAdministrationStatusDisplay(currentStatus)

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={648}
      title={
        <TitleContainer>
          {currentStatusDisplay.display}
          <ModalTitle>
            <Heading level={3}>{t('EDIT_ADMINISTRATION_RECORD')}</Heading>
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <Form form={form} initialValues={initialValues} preserve={false}>
        <ModalContentContainer>
          <FormCard level={2}>
            <FormItem
              name="outcome"
              label={<Text>{t('OUTCOME')}</Text>}
              required
              rules={[{ required: true, message: t('OUTCOME_REQUIRED') }]}
            >
              <StyledSelect
                placeholder={t('SELECT_OUTCOME')}
                data-testid="edit-administration-outcome"
              >
                {statusOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </StyledSelect>
            </FormItem>
            <FormItem
              name="comment"
              label={<Text>{t('COMMENT')}</Text>}
              full
              rules={[{ max: 150, message: t('COMMENT_MAX_LENGTH') }]}
            >
              <StyledTextArea
                rows={4}
                placeholder={t('ENTER_COMMENT')}
                maxLength={150}
                showCount
                data-testid="edit-administration-comment"
              />
            </FormItem>
          </FormCard>
        </ModalContentContainer>
      </Form>

      <Divider />

      <ButtonsContainer>
        <Button onClick={handleCancel} data-testid="cancel-btn">
          {t('CANCEL')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isLoading}
          isLoading={isLoading}
          data-testid="save-btn"
        >
          {t('SAVE')}
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const CardTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const MedicationNameHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledSelect = styled(Select)`
  width: 100%;
`

const StyledTextArea = styled(TextArea)`
  width: 100%;
  margin-bottom: 16px;
`

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`

export default AdministrationModal
